import React from "react";

/**
 * OriginMap — origin_markets evidence component.
 *
 * Ranked list of guest origin markets with diverging bars for avg_delta_f.
 * Negative delta = park was cooler than guest's home.
 * If a map library becomes available, upgrade this to a map with circles.
 * The ranked list is the spec-acceptable fallback (spec §7).
 *
 * Spec references: §7 (origin_markets).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Strip trailing " (guest home)" from location. */
const cleanLocation = (loc) => {
  if (!loc) return "";
  return loc.replace(/\s*\(guest home\)\s*$/, "");
};

const OriginMap = ({ panel }) => {
  const f = panel.facts || {};
  const markets = f.markets || [];

  if (markets.length === 0) return null;

  // Find max absolute delta for bar scaling
  const maxAbsDelta = Math.max(
    ...markets.map((m) => Math.abs(Number(m.avg_delta_f) || 0)),
    1
  );

  return (
    <div className="space-y-4">
      {/* Diverging gradient legend */}
      <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2.5">
        <div className="flex items-center justify-between text-[11px] font-medium mb-1.5">
          <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M4 12l8 8 8-8" />
            </svg>
            Park cooler
          </span>
          <span className="text-gray-400 dark:text-gray-500 text-[10px]">temperature gap (°F)</span>
          <span className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400">
            Park warmer
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M4 12l8-8 8 8" />
            </svg>
          </span>
        </div>
        {/* Gradient bar — blue (cooler) on left, neutral in middle, orange (warmer) on right */}
        <div className="h-2 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 via-gray-200 dark:via-gray-600 to-orange-500" />
        <div className="flex justify-between mt-1 text-[10px] text-gray-400 dark:text-gray-500">
          <span>−{maxAbsDelta.toFixed(0)}°</span>
          <span>0°</span>
          <span>+{maxAbsDelta.toFixed(0)}°</span>
        </div>
      </div>

      {/* Ranked list */}
      <div className="space-y-1">
        {markets.map((m, i) => {
          const delta = Number(m.avg_delta_f) || 0;
          const sessions = Number(m.sessions) || 0;
          const homeHigh = Number(m.avg_home_high_f) || null;
          const isCooler = delta < 0;
          const barPct = (Math.abs(delta) / maxAbsDelta) * 100;

          return (
            <div
              key={i}
              className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {/* Rank badge */}
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 text-[10px] font-semibold text-gray-500 dark:text-gray-400 shrink-0">
                {i + 1}
              </span>

              {/* Location + sessions + home temp */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                  {cleanLocation(m.location)}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-2">
                  <span>{fmtNum(sessions)} sessions</span>
                  {homeHigh != null && (
                    <span className="flex items-center gap-0.5">
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span>home high {homeHigh.toFixed(0)}°</span>
                    </span>
                  )}
                </p>
              </div>

              {/* Diverging bar */}
              <div className="w-28 flex items-center shrink-0">
                <div className="relative w-full h-1.5 flex items-center">
                  <div className="absolute left-1/2 w-px h-3 bg-gray-300 dark:bg-gray-600" />
                  <div
                    className={`absolute h-1.5 rounded-full ${
                      isCooler ? "bg-blue-500 right-1/2" : "bg-orange-500 left-1/2"
                    }`}
                    style={{ width: `${barPct / 2}%` }}
                  />
                </div>
              </div>

              {/* Delta value */}
              <span
                className={`text-xs font-semibold w-14 text-right shrink-0 tabular-nums ${
                  isCooler
                    ? "text-blue-600 dark:text-blue-400"
                    : delta > 0
                      ? "text-orange-600 dark:text-orange-400"
                      : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {delta > 0 ? "+" : ""}{delta.toFixed(1)}°
              </span>
            </div>
          );
        })}
      </div>

      {/* Explanatory note */}
      <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
        Guest browsing locations and the temperature gap to the park on the same day.{" "}
        <span className="text-blue-600 dark:text-blue-400 font-medium">Negative</span> = the park was the cooler escape.{" "}
        <span className="text-orange-600 dark:text-orange-400 font-medium">Positive</span> = the park was warmer than home.
      </p>
    </div>
  );
};

export default OriginMap;
